/**
 * Activation des options d'apprentissage.
 *
 * Empilement à 100 % et non barres groupées : activé et désactivé sont les deux
 * parts d'un même tout, elles ne se comparent pas côte à côte mais se
 * partagent une longueur. C'est la forme part-à-tout, et elle rend la lecture
 * immédiate — la frontière entre les deux couleurs EST le taux d'activation.
 *
 * Deux séries, donc légende obligatoire : l'identité ne repose jamais sur la
 * seule couleur. Les deux teintes sont l'emplacement 1 et 2, validés ensemble.
 */

import {
  Bar, BarChart, CartesianGrid, LabelList, ResponsiveContainer, Tooltip, XAxis, YAxis,
} from 'recharts';

import { formatInteger, formatPercent } from '../lib/format';
import { AXIS_STYLE, BAR_MAX_SIZE, COLORS } from '../lib/theme';
import type { DataColumn, LearnOptionItem } from '../types';
import { ChartCard } from './Card';
import { ChartLegend, ChartTooltip, type LegendEntry } from './ChartTooltip';

const legend: readonly LegendEntry[] = [
  { label: 'Activé', color: COLORS.series1, shape: 'area' },
  { label: 'Désactivé', color: COLORS.series2, shape: 'area' },
];

const columns: ReadonlyArray<DataColumn<LearnOptionItem>> = [
  { key: 'label', header: 'Option', render: (row) => row.label },
  { key: 'enabled', header: 'Activé', numeric: true, render: (row) => formatInteger(row.enabled) },
  { key: 'disabled', header: 'Désactivé', numeric: true, render: (row) => formatInteger(row.disabled) },
  { key: 'share', header: "Taux d'activation", numeric: true, render: (row) => formatPercent(row.enabledShare) },
];

interface Row extends LearnOptionItem {
  enabledPct: number;
  disabledPct: number;
}

export function LearnOptionsChart({ data }: { data: readonly LearnOptionItem[] }) {
  // Les deux parts sont converties en pourcentages : les options n'ont pas
  // toutes le meme nombre d'apprentissages renseignes, et des longueurs
  // absolues differentes empecheraient de comparer les taux d'une ligne a
  // l'autre — ce qui est precisement la question posee.
  const rows: Row[] = data.map((item) => {
    const total = item.enabled + item.disabled;
    return {
      ...item,
      enabledPct: total > 0 ? (item.enabled / total) * 100 : 0,
      disabledPct: total > 0 ? (item.disabled / total) * 100 : 0,
    };
  });

  return (
    <ChartCard
      title="Options d'apprentissage"
      subtitle="Part des apprentissages où chaque option est active"
      rows={rows}
      columns={columns}
      chartHeight={230}
    >
      <ChartLegend entries={legend} />
      <ResponsiveContainer width="100%" height="85%">
        <BarChart data={rows} layout="vertical" margin={{ top: 4, right: 56, bottom: 0, left: 0 }}>
          <CartesianGrid stroke={COLORS.grid} horizontal={false} />
          <XAxis
            type="number"
            domain={[0, 100]}
            tickFormatter={(value: number) => `${value}\u202F%`}
            tick={{ fill: AXIS_STYLE.fill, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
          />
          <YAxis
            type="category"
            dataKey="label"
            tick={{ fill: COLORS.textSecondary, fontSize: AXIS_STYLE.fontSize }}
            axisLine={false}
            tickLine={false}
            width={130}
          />
          <Tooltip
            cursor={{ fill: 'rgba(255,255,255,0.05)' }}
            content={<ChartTooltip valueFormatter={(v) => formatPercent(v)} />}
          />
          {/* Rayon nul entre les deux segments, arrondi aux deux extremites de
              la barre : l'arrondi marque la fin de la donnee, pas la frontiere
              interne. */}
          <Bar
            isAnimationActive={false}
            dataKey="enabledPct"
            stackId="opt"
            name="Activé"
            fill={COLORS.series1}
            // Contour de 2 px dans la couleur de la surface : c'est lui qui
            // separe les deux segments. Un trait de separation ajouterait de
            // l'encre qui n'est pas de la donnee ; le vide, non.
            stroke={COLORS.surface}
            strokeWidth={2}
            maxBarSize={BAR_MAX_SIZE}
          />
          <Bar
            isAnimationActive={false}
            dataKey="disabledPct"
            stackId="opt"
            name="Désactivé"
            fill={COLORS.series2}
            stroke={COLORS.surface}
            strokeWidth={2}
            radius={[0, 4, 4, 0]}
            maxBarSize={BAR_MAX_SIZE}
          >
            {/* L'etiquette est posee au bout de la barre ENTIERE, pas au bout
                du segment « active » : sur une option peu utilisee, elle se
                serait retrouvee par-dessus le segment voisin, en texte clair
                sur un aplat clair. */}
            <LabelList
              dataKey="enabledShare"
              position="right"
              fill={COLORS.textSecondary}
              fontSize={12}
              formatter={(value: unknown) => formatPercent(Number(value))}
            />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </ChartCard>
  );
}
